import React, { useState, useCallback, useEffect, useRef } from "react";
import * as AuthHelpers from "../common/helpers/auth";
import { withRouter, useHistory } from "react-router-dom";
import jwtDecode from "jwt-decode";
import * as Api from "../api";
import Cookies from "js-cookie";
import { currentUserQuery } from "../api/userQueries";
import { useApi as useApiRedux } from "../api/useApiRedux";
import { usersInEnabledWorkGroups } from "../api/userQueries";
import { useApi } from "../api/useApi";
import {
  getCachedAuthConfig,
  fetchAuthConfig,
  isAcesWebProvider,
} from "../services/authConfigService";
export const AuthContext = React.createContext();

const AuthProvider = (props) => {
  const [{ errors, data }, doFetch] = useApiRedux();

  //Init Data Fetch
  const [
    { loading: groupDataLoading, data: groupData, errors: checkGroupErrors },
    doCheckUserGroup,
  ] = useApi();

  const userObject = AuthHelpers.buildUser();
  const [user, setUser] = useState(userObject ? userObject : null);
  // Mirror of `user` so revalidateSession can detect a downgrade without
  // depending on `user` (which would re-register the listeners every login).
  const userRef = useRef(userObject ? userObject : null);
  useEffect(() => {
    userRef.current = user;
  }, [user]);
  const switchingUserRef = useRef(false);
  const acesExchangingRef = useRef(false);
  // When this tab's current session began; switch signals older than this
  // were already reflected when the tab loaded or signed in.
  const sessionStartedAtRef = useRef(Date.now());
  const [userPermissioned, setUserPermissioned] = useState(false);
  const [loginErrors, setLoginErrors] = useState(null);
  const [authConfig, setAuthConfig] = useState(() => getCachedAuthConfig());
  const [acesLoginPending, setAcesLoginPending] = useState(false);
  const acesAbortControllerRef = useRef(null);
  let history = useHistory();

  // Fetch auth config on mount (stale-while-revalidate)
  useEffect(() => {
    fetchAuthConfig().then((config) => {
      setAuthConfig((prev) => {
        // Only update if changed to avoid unnecessary re-renders
        if (JSON.stringify(prev) !== JSON.stringify(config)) {
          return config;
        }
        return prev;
      });
    });
  }, []);

  /* Leaves for the cookie's current user when it no longer matches this tab's
     (another tab signed in as someone else, or dropped the token). Full
     navigation, so every piece of in-memory state is rebuilt for that user. */
  const reloadAsCookieUser = useCallback(() => {
    switchingUserRef.current = true;
    window.location.href = "/welcome";
  }, []);

  /* Re-check the session against the token cookie. Downgrade-only: may
     transition user truthy → null (logged out elsewhere, or token expired),
     or reload the tab when the cookie now holds a different user, but never
     upgrades in place or writes storage — upgrading from here would
     re-fire the post-login navigation effects (redirect loops), and the
     no-op path never calls setUser so dependent effects (workgroup check,
     websocket) don't re-run on every navigation.

     AcesWeb tenants don't have a BaseCap logoutUrl page of their own, so on
     downgrade the tab leaves for the AcesWeb logoutUrl immediately (full page
     navigation — never history.push, and never a redirect-url write; the
     shared key can't represent multiple redirecting tabs, see AC-14928).
     AzureAD tenants keep the in-place login prompt via ProtectedRoute.

     The exception is a token another tab dropped while signing someone in:
     leaving for logoutUrl then would end that user's AcesWeb session. Such a
     tab reloads instead. leaveForLogout is false for indirect nudges
     (sessionPhoto removal, storage.clear()), which also fire on that path. */
  const revalidateSession = useCallback(
    ({ leaveForLogout = true } = {}) => {
      // Mid-reload for a user switch, or mid-exchange in this tab: the cookie
      // is about to change, so neither is a logout.
      if (switchingUserRef.current || acesExchangingRef.current) {
        return;
      }
      const current = AuthHelpers.readUserFromToken();
      if (userRef.current && current) {
        if (current.id !== userRef.current.id) {
          reloadAsCookieUser();
        } else if (
          AuthHelpers.wasUserSwitchedSince(sessionStartedAtRef.current)
        ) {
          // A switch back to this tab's own user: nothing to reload, but mark
          // it seen so a later plain expiry still leaves for logoutUrl.
          sessionStartedAtRef.current = Date.now();
        }
        return;
      }
      if (userRef.current && !current) {
        // The signal outlives its event, so this also covers a tab that was
        // asleep when another tab's switch or failed switch happened.
        if (AuthHelpers.wasUserSwitchedSince(sessionStartedAtRef.current)) {
          reloadAsCookieUser();
          return;
        }
        userRef.current = null;
        setUser(null);
        if (
          leaveForLogout &&
          isAcesWebProvider(authConfig) &&
          authConfig.logoutUrl
        ) {
          window.location.href = authConfig.logoutUrl;
        }
      }
    },
    [authConfig, reloadAsCookieUser]
  );

  // Cross-tab session changes: another tab's logout() or login writes a signal key.
  useEffect(() => {
    const onStorage = (event) => {
      /* Another tab replaced or dropped the token cookie. Only a signed-in
         tab can be acting as the wrong user; a tab that is mid-exchange
         completes its own login and signals in turn. */
      if (event.key === AuthHelpers.USER_SWITCH_SIGNAL_KEY) {
        if (userRef.current && !acesExchangingRef.current) {
          if (AuthHelpers.readUserFromToken()?.id !== userRef.current.id) {
            reloadAsCookieUser();
          } else {
            sessionStartedAtRef.current = Date.now();
          }
        }
        return;
      }
      if (AuthHelpers.isSessionSignalKey(event.key)) {
        revalidateSession({
          leaveForLogout: event.key === AuthHelpers.LOGOUT_SIGNAL_KEY,
        });
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [revalidateSession, reloadAsCookieUser]);

  // Returning to a stale/expired tab: re-check before the user interacts.
  useEffect(() => {
    const onFocus = () => revalidateSession();
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        revalidateSession();
      }
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [revalidateSession]);

  // In-tab navigation: re-check on every route change.
  useEffect(() => {
    revalidateSession();
  }, [props.location.pathname, props.location.search, revalidateSession]);
  // Shared post-login flow: verify user via GraphQL, handle redirect, set sessionPhoto
  const completeLogin = useCallback(
    async (token, signal) => {
      /* Invariant: the token cookie must be set BEFORE `user` becomes truthy.
         revalidateSession() logs out whenever `user` is truthy but the cookie
         is missing/expired — setting user first would trigger a spurious
         logout on the next focus or route change. */
      Cookies.set("token", token);
      // Signal right away: other tabs send this cookie from now on, so they
      // must not keep showing a different user while the query runs.
      AuthHelpers.signalUserSwitch();
      sessionStartedAtRef.current = Date.now();
      // After the signal, so other tabs treat the removal as part of the
      // switch; the new user's photo, if any, is written below.
      localStorage.removeItem("sessionPhoto");

      const res = await Api.request(currentUserQuery, {}, signal);

      if (res?.data?.currentUser) {
        let redirectUrl;
        const localRedirectUrl = window.localStorage.getItem("redirect-url");
        if (localRedirectUrl && localRedirectUrl !== "/") {
          redirectUrl = localRedirectUrl;
        } else {
          redirectUrl = "/welcome";
        }

        // Clear the redirect URL from localStorage before redirecting
        window.localStorage.removeItem("redirect-url");

        if (res?.data?.currentUser?.profilePhoto) {
          window.localStorage.setItem(
            "sessionPhoto",
            res.data.currentUser.profilePhoto
          );
        }

        // The workgroup check re-runs for the new user; don't let a previous
        // user's result open protected pages in the meantime.
        setUserPermissioned(false);
        setUser(AuthHelpers.buildUser());
        history.push(redirectUrl);
        return true;
      }
      return false;
    },
    [history]
  );

  useEffect(() => {
    if (data) {
      const abortController = new AbortController();

      completeLogin(data, abortController.signal).catch((err) => {
        if (!abortController.signal.aborted)
          setLoginErrors([{ message: err.message }]);
      });

      return () => abortController.abort();
    }
  }, [data, completeLogin]);

  useEffect(() => {
    if (errors) {
      setUser(null);
      setLoginErrors([{ message: "Login Error, Please Try Again Later." }]);
    }
  }, [errors]);

  const login = useCallback(() => {
    // Store redirect URL for post-login navigation
    const fullRedirectUrl = `${props.location.pathname}${props.location.search}`;
    window.localStorage.setItem("redirect-url", fullRedirectUrl);

    if (isAcesWebProvider(authConfig)) {
      // AcesWeb: Redirect to external login URL
      if (authConfig.loginUrl) {
        window.location.href = authConfig.loginUrl;
      } else {
        console.error("AcesWeb login URL not configured");
      }
    } else {
      // Azure AD: Use existing OAuth2 flow
      const stateObject = JSON.stringify({
        origin: window.location.origin,
        redirect: `${window.location.origin}/login_redirect`,
      }).replace(/\\n/g, "");

      const nonce = Math.floor(1000000000 + Math.random() * 900000);
      const loginURL = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?response_type=id_token&scope=User.Read.All%20Group.Read.All%20openid%20profile&client_id=${
        process.env.REACT_APP_APPCLIENTID
      }&redirect_uri=${process.env.REACT_APP_LOGINURL}&state=${encodeURIComponent(
        stateObject
      )}&nonce=${nonce}&prompt=select_account&response_mode=form_post`;

      window.location.href = loginURL;
    }
  }, [props.location.pathname, props.location.search, authConfig]);

  const logout = useCallback(() => {
    // Clear local state for all providers
    Cookies.remove("token");
    /* Notify other tabs before navigating away. localStorage writes commit
       synchronously, so the signal lands before the redirect below — this
       must stay above the provider fork so both branches emit it. It also
       goes before the sessionPhoto removal: other tabs get events in write
       order, and that removal alone would downgrade them without leaving. */
    AuthHelpers.signalLogout();
    localStorage.removeItem("sessionPhoto");
    localStorage.removeItem("redirect-url");
    sessionStorage.clear();
    setUser(null);

    if (isAcesWebProvider(authConfig)) {
      // AcesWeb: Just clear local state, redirect to home
      window.location.href = authConfig.logoutUrl;
    } else {
      // Azure AD: Full logout including Azure AD session
      const logoutURL = `https://login.microsoftonline.com/common/oauth2/v2.0/logout?post_logout_redirect_uri=${encodeURIComponent(
        window.location.protocol + "//" + window.location.hostname
      )}`;
      window.location.href = logoutURL;
    }
  }, [authConfig]);

  const doLogin = useCallback(
    (authToken) => {
      const adToken = jwtDecode(authToken);

      const variables = { email: adToken.preferred_username, authToken };

      doFetch({ query: "/authentication", body: variables });
    },
    [doFetch]
  );

  const doAcesLogin = useCallback(
    async (authCode) => {
      // Abort any in-flight ACES login
      if (acesAbortControllerRef.current) {
        acesAbortControllerRef.current.abort();
      }
      const abortController = new AbortController();
      acesAbortControllerRef.current = abortController;

      setAcesLoginPending(true);
      setLoginErrors(null);
      acesExchangingRef.current = true;
      // With no valid token left in the cookie, other tabs still showing a
      // user would read it as a logout and leave for logoutUrl, ending the
      // AcesWeb session of whoever launched this page; tell them it's a switch.
      if (!AuthHelpers.readUserFromToken()) {
        AuthHelpers.signalUserSwitch();
      }

      /* A failed exchange must not leave the browser on a session that may
         belong to someone else, so drop the token before showing the error.
         The switch signal (not the logout one) makes other tabs reload signed
         out instead of leaving for logoutUrl, which would end the AcesWeb
         session of whoever launched this page. The signal must be written
         before anything removes sessionPhoto (the next render's buildUser()
         does), so other tabs act on the switch rather than that nudge. */
      const failLogin = (message) => {
        Cookies.remove("token");
        AuthHelpers.signalUserSwitch();
        // Cleared now, not on the next render, so a focus in between can't
        // read it as a downgrade and leave for logoutUrl.
        userRef.current = null;
        setUser(null);
        setLoginErrors([{ message }]);
        setAcesLoginPending(false);
      };

      try {
        const response = await fetch("/api/v1/aces-authentication", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ authCode }),
          signal: abortController.signal,
        });

        if (!response.ok) {
          failLogin("Authentication failed. Please try again.");
          return;
        }

        const data = await response.json();
        const token = data.token;

        if (!token) {
          failLogin("No token received from server.");
          return;
        }

        /* The auth code always identifies the AcesWeb user who launched this
           page, which may differ from whoever already holds the token cookie
           in this browser. Same user: refresh the token (it carries any group
           changes synced during the exchange) and leave other tabs alone.
           Different user: full login, which signals other tabs to reload. */
        const currentUserId = AuthHelpers.readUserFromToken()?.id;
        if (currentUserId && currentUserId === jwtDecode(token).actort) {
          Cookies.set("token", token);
          sessionStartedAtRef.current = Date.now();
          if (userRef.current?.id !== currentUserId) {
            setUserPermissioned(false);
          }
          setUser(AuthHelpers.buildUser());
          setAcesLoginPending(false);
          return;
        }

        const success = await completeLogin(token, abortController.signal);
        if (!success) {
          failLogin("Login Error, Please Try Again Later.");
          return;
        }
        setAcesLoginPending(false);
      } catch (error) {
        if (error.name === "AbortError") {
          return;
        }
        if (process.env.NODE_ENV !== "production") {
          console.error("Auth code exchange error:", error);
        }
        failLogin("Login Error, Please Try Again Later.");
      } finally {
        if (acesAbortControllerRef.current === abortController) {
          acesExchangingRef.current = false;
        }
      }
    },
    [completeLogin]
  );

  // Cleanup ACES abort controller on unmount
  useEffect(() => {
    return () => {
      if (acesAbortControllerRef.current) {
        acesAbortControllerRef.current.abort();
      }
    };
  }, []);

  const CheckGroup = useCallback(
    (userId) => {
      doCheckUserGroup({
        query: usersInEnabledWorkGroups,
        variables: {
          where: {
            id: { eq: Number(userId) },
            enabled: { eq: true },
          },
        },
      });
    },
    [doCheckUserGroup]
  );

  useEffect(() => {
    if (user?.id) {
      CheckGroup(user.id);
    }
  }, [CheckGroup, user]);

  useEffect(() => {
    if (groupData) {
      const workgroupTotal = groupData?.usersInEnabledWorkGroups?.totalCount;
      const isFoundInWorkgroup = workgroupTotal >= 1;
      setUserPermissioned(isFoundInWorkgroup);
    }
  }, [groupData, setUserPermissioned]);

  const value = React.useMemo(() => {
    return {
      user,
      loginErrors,
      login,
      logout,
      doLogin,
      doAcesLogin,
      acesLoginPending,
      userPermissioned,
      groupDataLoading,
      checkGroupErrors,
      authConfig,
    };
  }, [
    user,
    doLogin,
    doAcesLogin,
    acesLoginPending,
    loginErrors,
    login,
    logout,
    userPermissioned,
    groupDataLoading,
    checkGroupErrors,
    authConfig,
  ]);

  return (
    <AuthContext.Provider value={value}>{props.children}</AuthContext.Provider>
  );
};

const AuthProviderWithRouter = withRouter(AuthProvider);
export default AuthProviderWithRouter;
